import { useEffect, useState } from 'react';
import { usePaymentMethods } from '../../store/payment-methods';
import { MerchantFeatures } from '../../constants';
import { useFeature } from '../use-features';

interface InstallmentCountry {
  countryCode: string;
  isInstallmentsAllowed: boolean;
  paymentResponsible: 'end_user' | 'merchant';
  cost: 'link' | 'issuer';
  enabled: boolean;
  available: boolean;
  hasNote?: boolean;
  merchantFeature?: MerchantFeatures;
  payerSelectable?: boolean;
}

const countries = [
  {
    countryCode: 'AR',
    isInstallmentsAllowed: false,
    paymentResponsible: 'buyer',
    cost: 'link',
    enabled: false,
    available: true,
    hasNote: true,
    payerSelectable: true,
  },
  {
    countryCode: 'BO',
    isInstallmentsAllowed: true,
    paymentResponsible: 'buyer',
    cost: 'issuer',
    enabled: false,
    available: false,
    payerSelectable: false,
  },
  {
    countryCode: 'BR',
    isInstallmentsAllowed: true,
    paymentResponsible: 'buyer',
    cost: 'link',
    enabled: true,
    available: true,
    payerSelectable: true,
  },
  {
    countryCode: 'CL',
    isInstallmentsAllowed: true,
    paymentResponsible: 'buyer',
    cost: 'issuer',
    enabled: false,
    available: true,
    payerSelectable: false,
  },
  {
    countryCode: 'CO',
    isInstallmentsAllowed: true,
    paymentResponsible: 'buyer',
    cost: 'issuer',
    enabled: true,
    available: true,
    payerSelectable: false,
  },
  {
    countryCode: 'CR',
    isInstallmentsAllowed: true,
    paymentResponsible: 'buyer',
    cost: 'issuer',
    enabled: false,
    available: false,
    payerSelectable: false,
  },
  {
    countryCode: 'EC',
    isInstallmentsAllowed: true,
    paymentResponsible: 'buyer',
    cost: 'issuer',
    enabled: false,
    available: true,
    payerSelectable: false,
  },
  {
    countryCode: 'GT',
    isInstallmentsAllowed: true,
    paymentResponsible: 'buyer',
    cost: 'issuer',
    enabled: false,
    available: false,
    payerSelectable: false,
  },
  {
    countryCode: 'MX',
    isInstallmentsAllowed: true,
    paymentResponsible: 'buyer',
    cost: 'link',
    enabled: false,
    available: true,
    payerSelectable: true,
  },
  {
    countryCode: 'PA',
    isInstallmentsAllowed: true,
    paymentResponsible: 'buyer',
    cost: 'issuer',
    enabled: false,
    available: false,
    payerSelectable: false,
  },
  {
    countryCode: 'PY',
    isInstallmentsAllowed: true,
    paymentResponsible: 'buyer',
    cost: 'issuer',
    enabled: false,
    available: true,
    payerSelectable: false,
  },
  {
    countryCode: 'PE',
    isInstallmentsAllowed: true,
    paymentResponsible: 'buyer',
    cost: 'issuer',
    enabled: false,
    available: true,
    payerSelectable: false,
  },
  {
    countryCode: 'UY',
    isInstallmentsAllowed: true,
    paymentResponsible: 'merchant',
    cost: 'link',
    enabled: false,
    available: true,
    payerSelectable: false,
  },
  {
    countryCode: 'UX',
    isInstallmentsAllowed: true,
    paymentResponsible: 'merchant',
    cost: 'link',
    enabled: false,
    available: true,
    payerSelectable: false,
    merchantFeature: MerchantFeatures.USD_UY_CHECKOUTS,
  },
] as InstallmentCountry[];

const applyArgentinaInstallmentsUi = (
  country: InstallmentCountry,
  countryInstallmentInfo: { isAllowed: boolean; isEnabled: boolean; paymentResponsible: string }
): InstallmentCountry => {
  const isAllowed = countryInstallmentInfo.isAllowed;
  const isRestrictedArgentina = country.countryCode === 'AR' && !isAllowed;

  if (isRestrictedArgentina) {
    return {
      ...country,
      isInstallmentsAllowed: false,
      enabled: false,
      available: false,
      hasNote: false,
      paymentResponsible: countryInstallmentInfo.paymentResponsible as InstallmentCountry['paymentResponsible'],
    };
  }

  return {
    ...country,
    isInstallmentsAllowed: isAllowed,
    enabled: countryInstallmentInfo.isEnabled,
    paymentResponsible: countryInstallmentInfo.paymentResponsible as InstallmentCountry['paymentResponsible'],
  };
};

export const useInstallmentsInfo = () => {
  const { hasFeature } = useFeature();

  const {
    installmentsCountryInfos: info,
    fetchInstallmentsCountryInfos,
    enableInstallmentsCountry,
    disableInstallmentsCountry,
    loading,
  } = usePaymentMethods();

  const [installmentsCountryInfos, setInstallmentsCountryInfos] = useState<typeof countries>([]);

  useEffect(() => {
    fetchInstallmentsCountryInfos();
  }, []);

  useEffect(() => {
    if (info) {
      const mappedInfo: InstallmentCountry[] = countries
        .map(country => {
          const countryInstallmentInfo = info.find(installmentInfo => installmentInfo.country == country.countryCode);

          if (countryInstallmentInfo) {
            return applyArgentinaInstallmentsUi(country, countryInstallmentInfo);
          }

          return country;
        })
        .reduce((acc, country) => {
          if (country.merchantFeature && !hasFeature(MerchantFeatures.USD_UY_CHECKOUTS)) {
            return acc;
          }

          return [...acc, country];
        }, [] as InstallmentCountry[]);

      setInstallmentsCountryInfos(mappedInfo);
    }
  }, [info]);

  return { installmentsCountryInfos, enableInstallmentsCountry, disableInstallmentsCountry, loading };
};
